import { Box, Button, FormField, Header, Modal, Select, SelectProps, SpaceBetween } from "@amzn/awsui-components-react";
import { Order } from "@amzn/ito-client";
import { FunctionComponent, useState } from "react";

export const CANCELLATION_REASONS: SelectProps.Option[] = [
    { label: "Duplicate order", value: "Duplicate order" },
    { label: "Order placed by mistake", value: "Order placed by mistake" },
    { label: "Employee offboarding", value: "Employee offboarding" },
    { label: "Budget constraints", value: "Budget constraints" },
    { label: "Item no longer needed", value: "Item no longer needed" },
    { label: "Requester requested cancellation", value: "Requester requested cancellation" },
    { label: "Other", value: "Other" },
];

export interface CancelOrderModalProps {
    visible: boolean;
    order: Order;
    onDismiss: () => void;
    onConfirm: (reason: string) => void;
    isSubmitting: boolean;
}

export const CancelOrderModal: FunctionComponent<CancelOrderModalProps> = ({
    visible,
    order,
    onDismiss,
    onConfirm,
    isSubmitting,
}) => {
    const [selectedOption, setSelectedOption] = useState<SelectProps.Option | null>(null);
    const [reasonError, setReasonError] = useState("");

    const itemCount = ((order as any).items ?? []).length;

    const handleConfirm = () => {
        if (!selectedOption) {
            setReasonError("Cancellation reason is required.");
            return;
        }
        setReasonError("");
        onConfirm(selectedOption.value!);
    };

    const handleDismiss = () => {
        setSelectedOption(null);
        setReasonError("");
        onDismiss();
    };

    return (
        <Modal
            data-testid="cancel-order-modal"
            visible={visible}
            onDismiss={handleDismiss}
            header={<Header variant="h2">Cancel Order</Header>}
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button
                            data-testid="cancel-modal-dismiss-button"
                            variant="link"
                            onClick={handleDismiss}
                            disabled={isSubmitting}
                        >
                            Keep order
                        </Button>
                        <Button
                            data-testid="cancel-modal-confirm-button"
                            variant="primary"
                            onClick={handleConfirm}
                            loading={isSubmitting}
                            disabled={isSubmitting}
                        >
                            Confirm
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween direction="vertical" size="m">
                <SpaceBetween direction="vertical" size="xxs">
                    <Box>Order ID: <strong>{order.orderReference}</strong></Box>
                    <Box>Items to cancel: <strong>{itemCount}</strong></Box>
                </SpaceBetween>
                <FormField
                    label="Cancellation reason"
                    errorText={reasonError}
                    constraintText="Required"
                >
                    <Select
                        data-testid="cancellation-reason-select"
                        selectedOption={selectedOption}
                        onChange={({ detail }) => {
                            setSelectedOption(detail.selectedOption);
                            if (reasonError) {
                                setReasonError("");
                            }
                        }}
                        options={CANCELLATION_REASONS}
                        placeholder="Select a cancellation reason"
                        disabled={isSubmitting}
                    />
                </FormField>
            </SpaceBetween>
        </Modal>
    );
};
